import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

type AuthMode = 'signin' | 'signup';

const STATS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '12,480', label: 'Assets tracked' },
  { value: '99.98%', label: 'Uptime' },
  { value: '24/7', label: 'Monitoring' },
];

/**
 * Two-column split shell for the auth flow (login / signup).
 *
 * Left: dark navy brand panel with a faint cyan map-grid texture and a dashed
 * circular route accent. Right: the form, with a pill toggle (Sign in / Free
 * trial) pinned to the top-right. Below 760px the brand panel is hidden and the
 * form goes full-width.
 */
export function AuthShell({ mode, children }: { mode: AuthMode; children: ReactNode }) {
  return (
    <div className="grid min-h-screen grid-cols-1 bg-background min-[760px]:grid-cols-[1.05fr_minmax(440px,0.95fr)]">
      <BrandPanel />

      <main className="relative flex min-h-screen flex-col px-6 py-8 min-[760px]:px-12 min-[760px]:py-10">
        <div className="flex justify-end">
          <AuthToggle mode={mode} />
        </div>
        <div className="flex flex-1 items-center justify-center py-8">
          <div className="w-full max-w-md">{children}</div>
        </div>
      </main>
    </div>
  );
}

function BrandPanel() {
  return (
    <aside
      className="relative hidden flex-col justify-between overflow-hidden p-10 text-foreground min-[760px]:flex min-[760px]:p-12"
      style={{
        backgroundImage:
          'radial-gradient(120% 120% at 0% 0%, #0e1830 0%, #0a1222 45%, #060a12 100%)',
      }}
      aria-hidden="false"
    >
      {/* Faint cyan map-grid texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(56,189,248,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.07) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(120% 100% at 50% 40%, #000 35%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(120% 100% at 50% 40%, #000 35%, transparent 100%)',
        }}
      />
      {/* Dashed circular route accent */}
      <div
        className="pointer-events-none absolute -bottom-40 -right-40 h-[480px] w-[480px] rounded-full border-2 border-dashed"
        style={{ borderColor: 'rgba(56,189,248,0.22)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-24 -right-24 h-[300px] w-[300px] rounded-full border border-dashed"
        style={{ borderColor: 'rgba(34,211,238,0.18)' }}
      />

      {/* Logo */}
      <div className="relative flex items-center">
        <img
          src="/logo.png"
          alt="Fleet Iris"
          className="h-9 w-auto select-none"
          draggable={false}
        />
      </div>

      {/* Headline */}
      <div className="relative max-w-md">
        <h2 className="text-3xl font-semibold leading-tight tracking-tight min-[760px]:text-4xl">
          Run your whole fleet from one Command Center
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Live telemetry, attendance, fuel, maintenance, and payroll — every asset and field user in
          a single tactical view.
        </p>
      </div>

      {/* Live stats */}
      <dl className="relative grid grid-cols-3 gap-4">
        {STATS.map((stat) => (
          <div key={stat.label}>
            <dt className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              {stat.label}
            </dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

function AuthToggle({ mode }: { mode: AuthMode }) {
  const base = 'rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150';
  const active = 'bg-primary text-primary-foreground shadow';
  const idle = 'text-muted-foreground hover:text-foreground';
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-border bg-surface/70 p-1"
      role="tablist"
      aria-label="Authentication mode"
    >
      <Link
        to="/login"
        role="tab"
        aria-selected={mode === 'signin'}
        className={cn(base, mode === 'signin' ? active : idle)}
      >
        Sign in
      </Link>
      <Link
        to="/signup"
        role="tab"
        aria-selected={mode === 'signup'}
        className={cn(base, mode === 'signup' ? active : idle)}
      >
        Free trial
      </Link>
    </div>
  );
}
